import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { UserAvatar } from './UserAvatar';
import TestsPage from './TestsPage';
import HomeworkPage from './HomeworkPage';
import ProfilePhotosPage from './ProfilePhotosPage';
import ClassSummaryPage from './ClassSummaryPage';
import axios from 'axios';
import { toast } from 'sonner';
import { useModules } from '../hooks/useModules';

// Import new components
import SubjectManagement from './SubjectManagement';
import TimetableManagement from './TimetableManagement';
import GradeManagement from './GradeManagement';
import AttendanceManagement from './AttendanceManagement';
import MessagingSystem from './MessagingSystem';
import SchoolLifeManagement from './SchoolLifeManagement';
import BillingManagement from './BillingManagement';
import TenantPendingPaymentsManagement from './TenantPendingPaymentsManagement';
import CantineTransportManagement from './CantineTransportManagement';
import UserManagement from './UserManagement';
import { SidebarBrand } from './SidebarBrand';
import { RouteRoleGuard } from './RouteRoleGuard';
import ParentDashboard from './ParentDashboard';
import StudentParentManagement from './StudentParentManagement';
import ClassManagement from './ClassManagement';
import UserCreation from './UserCreation';
import TimetableManagementAdvanced from './TimetableManagementAdvanced';
import MyTimetable from './MyTimetable';
import SubscriptionManager from './SubscriptionManager';
// import SubscriptionDialog from './SubscriptionDialog'; // Supprimé - Utiliser SubscriptionRequired
import SuperAdminDashboard from './SuperAdminDashboard';
import AdminNotifications from './AdminNotifications';
import EnrollmentCodeDisplay from './EnrollmentCodeDisplay';
import ReportCardManagement from './ReportCardManagement';
import SettingsPage from './SettingsPage';
import TenantConfiguration from './TenantConfiguration';
import PermissionsManagement from './PermissionsManagement';
import StudentPromotionManagement from './StudentPromotionManagement';
import TreasurerDashboard from './TreasurerDashboard';
import SecretaryDashboard from './SecretaryDashboard';
import ParentMealReservation from './ParentMealReservation';
import MealReservationValidation from './MealReservationValidation';
import TransportSubscriptionValidation from './TransportSubscriptionValidation';
import ParentTransportReservation from './ParentTransportReservation';
import ProtectedRoute from './ProtectedRoute';
import BaseModulesSubscriptionDialog from './BaseModulesSubscriptionDialog';
import DocumentManagement from './DocumentManagement';
import TenantSuspendedModal from './TenantSuspendedModal';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Icons
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Calendar,
  MessageSquare,
  FileText,
  Settings,
  LogOut,
  GraduationCap,
  UserCheck,
  ClipboardList,
  TrendingUp,
  Menu,
  X,
  Shield,
  CreditCard,
  Utensils,
  Link as LinkIcon,
  UserPlus,
  Crown,
  Bell,
  Cog,
  CheckCircle,
  AlertCircle,
  ArrowUpCircle,
  Bus,
  Share2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dashboard home component
const DashboardHome = ({ user, isTenantSuspended, checkingTenantStatus }) => {
  const [stats, setStats] = useState({
    total_students: 0,
    total_teachers: 0,
    total_classes: 0,
    recent_activity: []
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Attendre que la vérification du tenant soit terminée
    if (checkingTenantStatus) {
      return;
    }

    // Ne pas faire d'appels API si le tenant est suspendu
    if (!isTenantSuspended) {
      fetchDashboardStats();
    } else {
      setLoading(false);
    }
  }, [isTenantSuspended, checkingTenantStatus]);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Ne pas afficher d'erreur si le tenant est suspendu
      if (!isTenantSuspended) {
        toast.error('Erreur lors du chargement des statistiques');
      }
    } finally {
      setLoading(false);
    }
  };

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  const getRoleLabel = (role) => {
    const roleMap = {
      STUDENT: 'Élève',
      PARENT: 'Parent',
      TEACHER: 'Enseignant(e)',
      ADMIN: 'Administrateur',
      PRINCIPAL: 'Directeur/trice',
      LIFE_COUNSELOR: 'CPE',
      NURSE: 'Infirmier/ère',
      LIBRARIAN: 'Documentaliste',
      ACCOUNTANT: 'Comptable',
      TREASURER: 'Trésorier',
      SECRETARY: 'Secrétaire',
      SUPER_ADMIN: 'Super Administrateur'
    };
    return roleMap[role] || role;
  };

  const getQuickActions = () => {
    switch (user.role) {
      case 'TEACHER':
        return [
          { icon: BookOpen, label: 'Mon Emploi du Temps', href: '/dashboard/my-schedule', color: 'bg-blue-500' },
          { icon: ClipboardList, label: 'Saisir notes', href: '/dashboard/grades', color: 'bg-green-500' },
          { icon: BookOpen, label: 'Devoirs', href: '/dashboard/homework', color: 'bg-orange-500' },
          { icon: MessageSquare, label: 'Messages', href: '/dashboard/messages', color: 'bg-purple-500' }
        ];
      
      case 'STUDENT':
        return [
          { icon: Calendar, label: 'Mon Emploi du Temps', href: '/dashboard/my-schedule', color: 'bg-blue-500' },
          { icon: TrendingUp, label: 'Mes notes', href: '/dashboard/grades', color: 'bg-green-500' },
          { icon: FileText, label: 'Devoirs', href: '/dashboard/homework', color: 'bg-orange-500' },
          { icon: MessageSquare, label: 'Messages', href: '/dashboard/messages', color: 'bg-purple-500' }
        ];
      
      case 'PARENT':
        return [
          { icon: Users, label: 'Mes enfants', href: '/dashboard/children', color: 'bg-blue-500' },
          { icon: Calendar, label: 'Emploi du temps enfants', href: '/dashboard/my-schedule', color: 'bg-blue-600' },
          { icon: TrendingUp, label: 'Suivi notes', href: '/dashboard/grades', color: 'bg-green-500' },
          { icon: UserCheck, label: 'Absences', href: '/dashboard/attendance', color: 'bg-orange-500' },
          { icon: MessageSquare, label: 'Communication', href: '/dashboard/messages', color: 'bg-purple-500' }
        ];
      
      default: // ADMIN, PRINCIPAL, etc.
        return [
          { icon: UserPlus, label: 'Créer utilisateurs', href: '/dashboard/create-users', color: 'bg-blue-500' },
          { icon: GraduationCap, label: 'Gestion Classes', href: '/dashboard/classes', color: 'bg-green-500' },
          { icon: LinkIcon, label: 'Relations Parent-Enfant', href: '/dashboard/student-parent', color: 'bg-purple-500' },
          { icon: CreditCard, label: 'Facturation', href: '/dashboard/billing', color: 'bg-orange-500' }
        ];
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-gray-200 rounded-lg" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="dashboard-home">
      {/* Welcome Header */}
      <div className="card gradient-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getWelcomeMessage()}, {user.first_name} !
            </h1>
            <div className="text-gray-600 mt-1">
              Vous êtes connecté(e) en tant que <Badge className="badge-primary ml-1">{getRoleLabel(user.role)}</Badge>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm text-gray-500">Dernière connexion</p>
              <p className="font-medium">
                {user.last_login ? new Date(user.last_login).toLocaleDateString('fr-FR') : 'Première connexion'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Only for admin roles */}
      {(user.role === 'ADMIN' || user.role === 'PRINCIPAL') && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Élèves</CardTitle>
              <GraduationCap className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.total_students}</div>
              <p className="text-xs text-gray-600 mt-1">
                Élèves inscrits dans l'établissement
              </p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Enseignants</CardTitle>
              <Users className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.total_teachers}</div>
              <p className="text-xs text-gray-600 mt-1">
                Enseignants actifs
              </p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Classes</CardTitle>
              <BookOpen className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{stats.total_classes}</div>
              <p className="text-xs text-gray-600 mt-1">
                Classes ouvertes cette année
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Actions rapides</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {getQuickActions().map((action, index) => {
            const IconComponent = action.icon;
            return (
              <Link key={index} to={action.href}>
                <Card className="card hover:scale-105 transition-transform cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex flex-col items-center text-center space-y-2">
                      <div className={`p-3 rounded-full ${action.color} bg-opacity-10`}>
                        <IconComponent className={`w-6 h-6 ${action.color.replace('bg-', 'text-')}`} />
                      </div>
                      <span className="font-medium text-sm">{action.label}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      {stats.recent_activity.length > 0 && (
        <Card className="card">
          <CardHeader>
            <CardTitle>Activité récente</CardTitle>
            <CardDescription>Les dernières actions dans votre établissement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_activity.map((activity, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-sm">{activity}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notifications Section for Admins */}
      <AdminNotifications user={user} isTenantSuspended={isTenantSuspended} />

      {/* Coming Soon Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-600" />
              Emploi du temps
            </CardTitle>
            <CardDescription>Consultez vos cours de la semaine</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 text-sm mb-4">
              Vos cours de la semaine, salles et horaires.
            </p>
            <Button variant="outline" asChild data-testid="home-open-schedule-btn">
              <Link to="/dashboard/my-schedule">Ouvrir mon emploi du temps</Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-green-600" />
              Messagerie
            </CardTitle>
            <CardDescription>Communiquez avec l'équipe éducative</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 text-sm mb-4">
              Échangez avec les familles et l’équipe éducative.
            </p>
            <Button variant="outline" asChild data-testid="home-open-messages-btn">
              <Link to="/dashboard/messages">Ouvrir la messagerie</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Dashboard component with navigation
const Dashboard = ({ user: initialUser, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(initialUser);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [showBaseModulesDialog, setShowBaseModulesDialog] = useState(false);
  const [checkingBaseModules, setCheckingBaseModules] = useState(true);
  const [hasBaseModulesSubscription, setHasBaseModulesSubscription] = useState(false);
  const location = useLocation();
  
  // État pour gérer la suspension du tenant
  const [isTenantSuspended, setIsTenantSuspended] = useState(false);
  const [checkingTenantStatus, setCheckingTenantStatus] = useState(true);
  
  // Hook pour gérer les modules activés - passé isTenantSuspended et checkingTenantStatus
  const { modules, loading: modulesLoading, isModuleEnabled, isAnyModuleEnabled } = useModules(isTenantSuspended, checkingTenantStatus);

  // Vérifier le statut du tenant ET l'abonnement en un seul appel - PRIORITAIRE
  useEffect(() => {
    const checkTenantStatusAndSubscription = async () => {
      // Ne pas vérifier pour SUPER_ADMIN
      if (user?.role === 'SUPER_ADMIN') {
        setCheckingTenantStatus(false);
        setCheckingSubscription(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/tenant/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Vérifier le statut is_active du tenant
        if (response.data?.tenant?.is_active === false) {
          setIsTenantSuspended(true);
        } else {
          // Si le tenant est actif, vérifier le statut de l'abonnement
          const status = response.data?.subscription?.status;
          setSubscriptionStatus(status);
          
          // Ne plus afficher l'ancien SubscriptionDialog
          // Le nouveau système utilise SubscriptionRequired qui gère NO_SUBSCRIPTION
          if ((user.role === 'ADMIN' || user.role === 'PRINCIPAL') && status === 'NO_SUBSCRIPTION') {
            console.log('NO_SUBSCRIPTION detected - SubscriptionRequired will handle it');
          }
        }
      } catch (error) {
        // Si erreur 403 avec TENANT_SUSPENDED, le tenant est suspendu (erreur attendue)
        if (error.response?.status === 403 && error.response?.data?.detail === "TENANT_SUSPENDED") {
          setIsTenantSuspended(true);
        } else if (error.response?.status === 404 && (user.role === 'ADMIN' || user.role === 'PRINCIPAL')) {
          // Ne plus afficher l'ancien dialog
          console.log('Subscription not found - SubscriptionRequired will handle it');
        } else {
          // Seulement logger les erreurs non-attendues
          console.error('Error checking tenant status:', error);
        }
      } finally {
        setCheckingTenantStatus(false);
        setCheckingSubscription(false);
      }
    };

    if (user) {
      checkTenantStatusAndSubscription();
    }
  }, [user]);

  // Intercepteur Axios pour détecter les erreurs de suspension
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403 && error.response?.data?.detail === "TENANT_SUSPENDED") {
          setIsTenantSuspended(true);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const handleLogoutFromSuspension = () => {
    setIsTenantSuspended(false);
    onLogout();
  };

  // Update user state when initialUser changes
  useEffect(() => {
    setUser(initialUser);
  }, [initialUser]);

  // Check base modules subscription for all roles except SUPER_ADMIN
  useEffect(() => {
    const checkBaseModulesSubscription = async () => {
      // Ne pas vérifier pour SUPER_ADMIN, STUDENT, ou PARENT
      if (!user || ['SUPER_ADMIN', 'STUDENT', 'PARENT'].includes(user.role)) {
        setCheckingBaseModules(false);
        setHasBaseModulesSubscription(true);
        return;
      }

      // Attendre que la vérification du tenant soit terminée
      if (checkingTenantStatus) {
        return;
      }

      // Ne pas vérifier si le tenant est suspendu
      if (isTenantSuspended) {
        setCheckingBaseModules(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/tenant/check-base-modules-subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const hasSubscribed = response.data.has_subscribed;
        setHasBaseModulesSubscription(hasSubscribed);

        // Bloquer l'accès si pas souscrit
        if (!hasSubscribed) {
          setShowBaseModulesDialog(true);
        }
      } catch (error) {
        console.error('Error checking base modules subscription:', error);
        // En cas d'erreur, bloquer par sécurité
        setShowBaseModulesDialog(true);
      } finally {
        setCheckingBaseModules(false);
      }
    };

    if (user) {
      checkBaseModulesSubscription();
    }
  }, [user, isTenantSuspended, checkingTenantStatus]);

  // Note: Le check de subscription est maintenant fusionné avec le check du statut du tenant ci-dessus

  const handleSubscriptionComplete = () => {
    setShowSubscriptionDialog(false);
    setSubscriptionStatus('ACTIVE');
    toast.success('Abonnement activé ! Rechargement du dashboard...');
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const handleBaseModulesSubscriptionSuccess = () => {
    setShowBaseModulesDialog(false);
    setHasBaseModulesSubscription(true);
    toast.success('Modules de base activés ! Rechargement du dashboard...');
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const navigation = [
    { name: 'Tableau de bord', href: '/dashboard', icon: LayoutDashboard, current: location.pathname === '/dashboard' },
    { name: 'Utilisateurs', href: '/dashboard/directory', icon: Users, roles: ['ADMIN'], requiredModule: 'users' },
    { name: 'Créer des utilisateurs', href: '/dashboard/create-users', icon: UserPlus, roles: ['ADMIN'], requiredModule: 'users' },
    { name: 'Relations Parent-Enfant', href: '/dashboard/student-parent', icon: LinkIcon, roles: ['ADMIN'], requiredModule: 'students_parents' },
    { name: 'Classes', href: '/dashboard/classes', icon: GraduationCap, roles: ['ADMIN', 'PRINCIPAL'], requiredModule: 'classes' },
    { name: 'Matières', href: '/dashboard/subjects', icon: BookOpen, roles: ['ADMIN', 'PRINCIPAL', 'TEACHER'], requiredModule: 'subjects' },
    { name: 'Emploi du temps', href: '/dashboard/timetable', icon: Calendar, roles: ['ADMIN', 'PRINCIPAL'], requiredModule: 'timetable' },
    { name: 'Mon Emploi du Temps', href: '/dashboard/my-schedule', icon: Calendar, roles: ['TEACHER', 'STUDENT', 'PARENT'], requiredModule: 'timetable' },
    { name: 'Abonnement', href: '/dashboard/subscription', icon: CreditCard, roles: ['ADMIN'] },
    { name: 'Code d\'Inscription', href: '/dashboard/enrollment-code', icon: Share2, roles: ['ADMIN'] },
    { name: 'Notes', href: '/dashboard/grades', icon: TrendingUp, roles: ['ADMIN', 'PRINCIPAL', 'TEACHER', 'SECRETARY', 'PARENT', 'STUDENT'], requiredModule: 'grades' },
    { name: 'Contrôles', href: '/dashboard/tests', icon: Calendar, roles: ['ADMIN', 'PRINCIPAL', 'TEACHER', 'SECRETARY', 'PARENT', 'STUDENT'], requiredModule: 'grades' },
    { name: 'Devoirs', href: '/dashboard/homework', icon: BookOpen, roles: ['ADMIN', 'TEACHER', 'PRINCIPAL', 'PARENT', 'STUDENT'], requiredModule: 'grades' },
    { name: 'Photos de profil', href: '/dashboard/photos', icon: Users, roles: ['PRINCIPAL', 'SECRETARY'] },
    { name: 'Bilan de classe', href: '/dashboard/class-summary', icon: TrendingUp, roles: ['PRINCIPAL', 'ADMIN'] },
    { name: 'Bulletins', href: '/dashboard/report-cards', icon: FileText, roles: ['ADMIN', 'PRINCIPAL', 'TEACHER', 'SECRETARY', 'PARENT', 'STUDENT'], requiredModule: 'reports' },
    { name: 'Absences', href: '/dashboard/attendance', icon: UserCheck, roles: ['ADMIN', 'PRINCIPAL', 'TEACHER', 'SECRETARY', 'PARENT', 'STUDENT'], requiredModule: 'attendance' },
    { name: 'Vie Scolaire', href: '/dashboard/school-life', icon: Shield, roles: ['TEACHER', 'LIFE_COUNSELOR', 'ADMIN', 'PRINCIPAL'], requiredModule: 'school_life' },
    { name: 'Facturation', href: '/dashboard/billing', icon: CreditCard, roles: ['ADMIN', 'ACCOUNTANT', 'PRINCIPAL', 'TREASURER'], requiredModule: 'billing' },
    { name: 'Validation Paiements', href: '/dashboard/pending-payments', icon: CheckCircle, roles: ['ADMIN', 'TREASURER', 'SECRETARY'], requiredModule: 'billing' },
    { name: 'Configuration', href: '/dashboard/configuration', icon: Cog, roles: ['ADMIN'] },
    { name: 'Permissions', href: '/dashboard/permissions', icon: Shield, roles: ['ADMIN'], requiredModule: 'permissions' },
    { name: 'Passage au Niveau Supérieur', href: '/dashboard/promotions', icon: ArrowUpCircle, roles: ['ADMIN'] },
    { name: 'Cantine & Transport', href: '/dashboard/canteen-transport', icon: Utensils, roles: ['ADMIN', 'ACCOUNTANT', 'TREASURER'], requiredModule: 'canteen' },
    { name: 'Réserver un Repas', href: '/dashboard/meal-reservation', icon: Utensils, roles: ['PARENT'], requiredModule: 'canteen' },
    { name: 'Abonnement Transport', href: '/dashboard/transport-reservation', icon: Bus, roles: ['PARENT'], requiredModule: 'transport' },
    { name: 'Valider Repas', href: '/dashboard/meal-validation', icon: CheckCircle, roles: ['ADMIN', 'TREASURER', 'SECRETARY'], requiredModule: 'canteen' },
    { name: 'Valider Transport', href: '/dashboard/transport-validation', icon: CheckCircle, roles: ['ADMIN', 'SECRETARY'], requiredModule: 'transport' },
    { name: 'Messages', href: '/dashboard/messages', icon: MessageSquare, requiredModule: 'messaging' },
    { name: 'Documents', href: '/dashboard/documents', icon: FileText, roles: ['ADMIN', 'PRINCIPAL', 'TEACHER', 'SECRETARY', 'TREASURER'], requiredModule: 'documents' },
    { name: 'Paramètres', href: '/dashboard/settings', icon: Settings },
  ];
  
  // Filtrer la navigation selon les modules activés et le rôle
  const filteredNavigation = navigation.filter(item => {
    // Si pas de rôle requis, afficher pour tous
    const roleMatch = !item.roles || item.roles.includes(user?.role);
    
    // Si pas de module requis, afficher
    // Si module requis, vérifier qu'il est activé
    const moduleMatch = !item.requiredModule || isModuleEnabled(item.requiredModule);
    
    return roleMatch && moduleMatch;
  });

  const ComingSoon = ({ title }) => (
    <div className="p-6 text-center">
      <div className="max-w-md mx-auto">
        <GraduationCap className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600 mb-6">
          Cette fonctionnalité sera bientôt disponible. Nous travaillons activement sur son développement.
        </p>
        <Link to="/dashboard">
          <Button>Retour au tableau de bord</Button>
        </Link>
      </div>
    </div>
  );

  return (
    <div className={`h-screen flex bg-gray-100 ${showSubscriptionDialog ? 'pointer-events-none' : ''}`}>
      {/* Overlay when subscription required */}
      {showSubscriptionDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
      )}

      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setSidebarOpen(false)} />
        
        <div className={`relative flex-1 flex flex-col max-w-xs w-full bg-white transition transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <SidebarBrand user={user} testId="sidebar-brand-mobile" />
            
            <nav className="mt-5 px-2 space-y-1">
              {filteredNavigation.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                      item.current
                        ? 'bg-blue-100 text-blue-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <IconComponent className="mr-4 h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
          
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Se déconnecter
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar - Flottante avec ombre */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 md:left-4 md:top-4 md:bottom-4 md:z-10">
        <div className="flex-1 flex flex-col min-h-0 border border-gray-200 bg-white rounded-xl shadow-lg">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <SidebarBrand user={user} testId="sidebar-brand" />
            
            <nav className="mt-5 flex-1 px-2 space-y-1">
              {filteredNavigation.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      item.current
                        ? 'bg-blue-100 text-blue-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <IconComponent className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
          
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center w-full">
              <UserAvatar person={user} size="sm" className="mr-3" testId="sidebar-user-avatar" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
              <button
                data-testid="logout-btn"
                onClick={onLogout}
                className="ml-3 p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Se déconnecter"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content - Ajusté pour sidebar flottante */}
      <div className="md:pl-72 flex flex-col flex-1">
        <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-white border-b border-gray-200">
          <button
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
        
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <RouteRoleGuard navigation={navigation} role={user?.role}>
          <Routes>
            <Route index element={
              user.role === 'SUPER_ADMIN' ? <SuperAdminDashboard user={user} /> :
              user.role === 'PARENT' ? <ParentDashboard user={user} /> : 
              user.role === 'TREASURER' ? <TreasurerDashboard user={user} /> :
              user.role === 'SECRETARY' ? <SecretaryDashboard user={user} /> :
              <DashboardHome user={user} isTenantSuspended={isTenantSuspended} checkingTenantStatus={checkingTenantStatus} />
            } />
            <Route path="super-admin" element={<SuperAdminDashboard user={user} />} />
            <Route path="treasurer" element={<TreasurerDashboard user={user} />} />
            <Route path="secretary" element={<SecretaryDashboard user={user} />} />
            <Route path="directory" element={<UserManagement user={user} />} />
            <Route path="create-users" element={<UserCreation user={user} />} />
            <Route path="student-parent" element={<StudentParentManagement user={user} />} />
            <Route path="classes" element={<ProtectedRoute requiredModule="classes" moduleName="Classes"><ClassManagement user={user} /></ProtectedRoute>} />
            <Route path="subjects" element={<ProtectedRoute requiredModule="subjects" moduleName="Matières"><SubjectManagement user={user} /></ProtectedRoute>} />
            <Route path="timetable" element={<ProtectedRoute requiredModule="timetable" moduleName="Emploi du temps"><TimetableManagementAdvanced user={user} /></ProtectedRoute>} />
            <Route path="my-schedule" element={<ProtectedRoute requiredModule="timetable" moduleName="Emploi du temps"><MyTimetable user={user} /></ProtectedRoute>} />
            <Route path="subscription" element={<SubscriptionManager user={user} />} />
            <Route path="grades" element={<ProtectedRoute requiredModule="grades" moduleName="Notes & Évaluations"><GradeManagement user={user} /></ProtectedRoute>} />
            <Route path="report-cards" element={<ProtectedRoute requiredModule="reports" moduleName="Bulletins"><ReportCardManagement user={user} /></ProtectedRoute>} />
            <Route path="attendance" element={<ProtectedRoute requiredModule="attendance" moduleName="Présences"><AttendanceManagement user={user} /></ProtectedRoute>} />
            <Route path="messages" element={<ProtectedRoute requiredModule="messaging" moduleName="Messagerie"><MessagingSystem user={user} /></ProtectedRoute>} />
            <Route path="school-life" element={<ProtectedRoute requiredModule="school_life" moduleName="Vie scolaire"><SchoolLifeManagement user={user} /></ProtectedRoute>} />
            <Route path="billing" element={<ProtectedRoute requiredModule="billing" moduleName="Facturation"><BillingManagement user={user} /></ProtectedRoute>} />
            <Route path="pending-payments" element={<TenantPendingPaymentsManagement user={user} />} />
            <Route path="configuration" element={<TenantConfiguration user={user} />} />
            <Route path="permissions" element={<ProtectedRoute requiredModule="permissions" moduleName="Permissions granulaires"><PermissionsManagement user={user} /></ProtectedRoute>} />
            <Route path="promotions" element={<StudentPromotionManagement user={user} />} />
            <Route path="canteen-transport" element={<ProtectedRoute requiredModule="canteen" moduleName="Cantine & Transport"><CantineTransportManagement user={user} /></ProtectedRoute>} />
            <Route path="meal-reservation" element={<ProtectedRoute requiredModule="canteen" moduleName="Cantine"><ParentMealReservation user={user} /></ProtectedRoute>} />
            <Route path="transport-reservation" element={<ProtectedRoute requiredModule="transport" moduleName="Transport scolaire"><ParentTransportReservation user={user} /></ProtectedRoute>} />
            <Route path="meal-validation" element={<ProtectedRoute requiredModule="canteen" moduleName="Cantine"><MealReservationValidation user={user} /></ProtectedRoute>} />
            <Route path="transport-validation" element={<ProtectedRoute requiredModule="transport" moduleName="Transport"><TransportSubscriptionValidation user={user} /></ProtectedRoute>} />
            <Route path="documents" element={<ProtectedRoute requiredModule="documents" moduleName="Gestion des Documents"><DocumentManagement user={user} /></ProtectedRoute>} />
            <Route path="enrollment-code" element={<EnrollmentCodeDisplay />} />
            <Route path="settings" element={<SettingsPage user={user} setUser={setUser} />} />
            <Route path="tests" element={<TestsPage user={user} />} />
            <Route path="homework" element={<HomeworkPage user={user} />} />
            <Route path="photos" element={<ProfilePhotosPage />} />
            <Route path="class-summary" element={<ClassSummaryPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </RouteRoleGuard>
        </main>
      </div>

      {/* Base Modules Subscription Dialog - Blocks access until subscribed */}
      <BaseModulesSubscriptionDialog
        open={showBaseModulesDialog}
        onSubscriptionSuccess={handleBaseModulesSubscriptionSuccess}
      />

      {/* Subscription Dialog - Non-closable modal */}
      {/* SubscriptionDialog supprimé - Utiliser SubscriptionRequired à la place */}
      {/* Le nouveau système utilise uniquement SubscriptionRequired avec le Pack de Base */}

      {/* Tenant Suspended Modal */}
      <TenantSuspendedModal
        open={isTenantSuspended}
        onLogout={handleLogoutFromSuspension}
      />
    </div>
  );
};

export default Dashboard;
